/**
 * Anthropic Messages API 适配器（T15a）。
 *
 * 只做「协议翻译」，网络层完全复用 T03 的 `http.ts`：
 * 超时、重试、取消、SSE 半行拼接、错误规范化都不在这里重写。
 *
 * 关键行为：
 * - 请求体按 Messages API：`system` 为顶层字段、`messages` 只含 user / assistant、
 *   `max_tokens` 必填（取 `settings.maxOutputTokens`）；
 * - 流式：解析 `content_block_delta` 的 `text_delta` / `thinking_delta` 与
 *   `message_stop`，映射为统一的 `StreamChunk`；
 * - 非流式：一次性产出思考与文本，再产出恰好一个 `done`；
 * - 取消：`signal.aborted` 后静默结束迭代，不产出任何分片（含 error），与 T03 契约一致；
 * - 流在 `message_stop` / 内联 error / 取消时提前结束时，主动释放底层 response body。
 */

import {
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  isAbortError,
  jsonHeaders,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  readSSEData,
  resolveDeps,
  streamHeaders,
  toProviderError,
  type OpenResponseResult,
} from './http';
import { readModelCatalog } from './modelCatalog';
import type {
  ChatRequest,
  FetchLike,
  ProviderAdapter,
  ProviderDeps,
  ResolvedProviderConfig,
  StreamChunk,
  StreamUsage,
  ToolSpec,
} from './types';

export const ANTHROPIC_KIND = 'anthropic' as const;
export const ANTHROPIC_VERSION = '2023-06-01';

/** 版本段：`/v1`、`/v1beta` 等。 */
const VERSION_SUFFIX = /\/v\d+[a-z0-9-]*$/i;

/** 拼出 `.../{version}/{segment}`；baseUrl 缺版本段时补 `fallbackVersion`。 */
function withVersion(rawBaseUrl: string, segment: string, fallbackVersion: string): string {
  const trimmed = (rawBaseUrl ?? '').trim();
  if (!trimmed) {
    throw new ProviderHttpError('baseUrl 为空', 'config');
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new ProviderHttpError(`baseUrl 非法: ${rawBaseUrl}`, 'config');
  }

  // 仅对 pathname 去尾斜杠；search / hash 原样保留。
  const pathname = parsed.pathname.replace(/\/+$/, '');
  parsed.pathname = VERSION_SUFFIX.test(pathname)
    ? `${pathname}${segment}`
    : `${pathname}${fallbackVersion}${segment}`;
  return parsed.toString();
}

/** `/v1/messages` 完整地址。 */
export function anthropicMessagesUrl(rawBaseUrl: string): string {
  return withVersion(rawBaseUrl, '/messages', '/v1');
}

/** `/v1/models` 完整地址（Anthropic Models API）。 */
export function anthropicModelsUrl(rawBaseUrl: string): string {
  return withVersion(rawBaseUrl, '/models', '/v1');
}

/**
 * Anthropic 请求头。
 *
 * 鉴权用 `x-api-key`（不是 OpenAI 的 `Authorization: Bearer`），
 * 因此这里传 `undefined` 给 T03 的头部工具，再自行补上专用字段。
 */
export function anthropicHeaders(options: {
  stream: boolean;
  apiKey?: string;
  extra?: Record<string, string>;
}): Record<string, string> {
  const { stream, apiKey, extra } = options;
  const base = stream ? streamHeaders(undefined, extra) : jsonHeaders(undefined, extra);
  const headers: Record<string, string> = { 'anthropic-version': ANTHROPIC_VERSION, ...base };
  if (apiKey) headers['x-api-key'] = apiKey;
  return headers;
}

/** Messages API 只允许 user / assistant；system 走顶层 `system` 字段。 */
export interface AnthropicMessage {
  role: 'user' | 'assistant';
  content: string | Record<string, unknown>[];
}

export interface AnthropicRequestBody {
  model: string;
  max_tokens: number;
  messages: AnthropicMessage[];
  stream: boolean;
  system?: string;
  temperature?: number;
  top_p?: number;
  thinking?: { type: 'enabled'; budget_tokens: number };
  tools?: Record<string, unknown>[];
  [key: string]: unknown;
}

/** 思考等级 -> Anthropic 扩展思考预算（token）。`off` 不下发 `thinking`。 */
const THINKING_BUDGET: Record<string, number | undefined> = {
  off: undefined,
  low: 1024,
  medium: 2048,
  high: 4096,
};

/** `max_tokens` 必填，且必须为正整数。 */
function normalizeMaxTokens(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  return Math.floor(value);
}

/**
 * 用户消息流里的 system 内容优先；只有没有用户注入时才使用 settings 回退提示词。
 * Anthropic 不接受 system 角色出现在 `messages` 里。
 */
function collectSystemPrompt(req: ChatRequest): string | undefined {
  const userParts: string[] = [];
  for (const message of req.messages) {
    if (message.role === 'system' && message.content.trim()) userParts.push(message.content);
  }
  if (userParts.length > 0) return userParts.join('\n\n');
  return req.settings.systemPrompt?.trim() ? req.settings.systemPrompt : undefined;
}

/** `data:` / `http(s):` 图片 URL -> Anthropic image source。 */
function imageSource(url: string): Record<string, unknown> | undefined {
  if (/^https?:\/\//i.test(url)) return { type: 'url', url };

  const comma = url.indexOf(',');
  if (url.startsWith('data:') && comma > 0 && url.slice(0, comma).includes(';base64')) {
    const header = url.slice(5, comma); // 例如 image/png;base64
    const mediaType = header.split(';')[0] ?? 'application/octet-stream';
    return { type: 'base64', media_type: mediaType, data: url.slice(comma + 1) };
  }
  return undefined;
}

/** 内部 ChatMessage -> Anthropic messages（多模态图片转 content blocks）。 */
export function toAnthropicMessages(req: ChatRequest): AnthropicMessage[] {
  const messages: AnthropicMessage[] = [];

  for (const message of req.messages) {
    if (message.role === 'system') continue;

    const role: AnthropicMessage['role'] = message.role === 'assistant' ? 'assistant' : 'user';
    const images = message.role === 'user' ? (message.images ?? []) : [];

    if (images.length === 0) {
      messages.push({ role, content: message.content });
      continue;
    }

    const parts: Record<string, unknown>[] = [];
    if (message.content) parts.push({ type: 'text', text: message.content });
    for (const url of images) {
      const source = imageSource(url);
      if (source) parts.push({ type: 'image', source });
    }
    messages.push({ role, content: parts });
  }

  return messages;
}

/** 内部 ToolSpec -> Anthropic `tools`（字段名是 `input_schema`）。 */
export function toAnthropicTools(tools?: ToolSpec[]): Record<string, unknown>[] | undefined {
  if (!tools || tools.length === 0) return undefined;
  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description,
    input_schema: tool.inputSchema,
  }));
}

/** 组装请求体；参数按 ModelSettings 映射。 */
export function buildAnthropicBody(req: ChatRequest): AnthropicRequestBody {
  const maxTokens = normalizeMaxTokens(req.settings.maxOutputTokens);
  const body: AnthropicRequestBody = {
    model: req.model,
    max_tokens: maxTokens,
    messages: toAnthropicMessages(req),
    stream: req.settings.stream,
    temperature: req.settings.temperature,
    top_p: req.settings.topP,
  };

  const system = collectSystemPrompt(req);
  if (system) body.system = system;

  const budget = THINKING_BUDGET[req.settings.thinkingLevel];
  if (budget !== undefined && maxTokens > budget) {
    body.thinking = { type: 'enabled', budget_tokens: budget };
    // Anthropic 扩展思考要求 temperature 固定为 1。
    body.temperature = 1;
  }

  const tools = toAnthropicTools(req.tools);
  if (tools) body.tools = tools;

  return body;
}

interface AnthropicDelta {
  type?: string;
  text?: string;
  thinking?: string;
  partial_json?: string;
}

interface AnthropicUsageShape {
  input_tokens?: number;
  output_tokens?: number;
}

interface AnthropicEvent {
  type?: string;
  index?: number;
  delta?: AnthropicDelta;
  content_block?: { type?: string; id?: string; name?: string };
  message?: { usage?: AnthropicUsageShape };
  usage?: AnthropicUsageShape;
  error?: { type?: string; message?: string };
}

interface AnthropicResponse {
  type?: string;
  content?: { type?: string; text?: string; thinking?: string; id?: string; name?: string; input?: unknown }[];
  usage?: AnthropicUsageShape;
  error?: { type?: string; message?: string };
}

export interface ParsedAnthropicPayload {
  chunks: StreamChunk[];
  /**
   * 部分用量：`message_start` 带 input_tokens，`message_delta` 带 output_tokens，
   * 由调用方合并后再挂到唯一的 `done` 上。
   */
  usage?: { promptTokens?: number; completionTokens?: number };
  /** 收到 `message_stop`：流已结束，调用方可提前释放连接。 */
  stop?: boolean;
}

function toUsage(usage?: AnthropicUsageShape): StreamUsage | undefined {
  if (!usage) return undefined;
  return {
    promptTokens: usage.input_tokens ?? 0,
    completionTokens: usage.output_tokens ?? 0,
  };
}

/**
 * 解析一条 SSE `data:` 负载为若干 StreamChunk。
 *
 * `readSSEData` 只回传 `data:` 内容（忽略 `event:` 行），而 Anthropic 每条
 * data 负载自带 `type` 字段，因此这里按 `type` 分派即可。
 */
export function mapAnthropicStreamPayload(payload: string): ParsedAnthropicPayload {
  const trimmed = payload.trim();
  if (!trimmed || trimmed === '[DONE]') return { chunks: [] };

  let parsed: AnthropicEvent;
  try {
    parsed = JSON.parse(trimmed) as AnthropicEvent;
  } catch {
    // 单个坏分片不应中断整个回答，跳过即可。
    return { chunks: [] };
  }

  if (parsed.type === 'error') {
    return {
      chunks: [{ type: 'error', error: parsed.error?.message ?? 'Provider 返回错误', code: 'http' }],
    };
  }

  const chunks: StreamChunk[] = [];

  if (parsed.type === 'content_block_delta') {
    const delta = parsed.delta;
    if (delta?.type === 'text_delta' && typeof delta.text === 'string' && delta.text.length > 0) {
      chunks.push({ type: 'text', delta: delta.text });
    }
    if (delta?.type === 'thinking_delta' && typeof delta.thinking === 'string' && delta.thinking.length > 0) {
      chunks.push({ type: 'reasoning', delta: delta.thinking });
    }
    if (delta?.type === 'input_json_delta' && typeof delta.partial_json === 'string') {
      chunks.push({
        type: 'tool_call',
        delta: JSON.stringify({ index: parsed.index ?? 0, arguments: delta.partial_json }),
      });
    }
  }
  if (parsed.type === 'content_block_start' && parsed.content_block?.type === 'tool_use') {
    chunks.push({
      type: 'tool_call',
      delta: JSON.stringify({
        index: parsed.index ?? 0,
        id: parsed.content_block.id,
        name: parsed.content_block.name,
        arguments: '',
      }),
    });
  }

  const result: ParsedAnthropicPayload = { chunks };

  const promptTokens = parsed.type === 'message_start' ? parsed.message?.usage?.input_tokens : undefined;
  const completionTokens = parsed.type === 'message_delta' ? parsed.usage?.output_tokens : undefined;
  if (typeof promptTokens === 'number' || typeof completionTokens === 'number') {
    result.usage = {};
    if (typeof promptTokens === 'number') result.usage.promptTokens = promptTokens;
    if (typeof completionTokens === 'number') result.usage.completionTokens = completionTokens;
  }

  if (parsed.type === 'message_stop') result.stop = true;
  return result;
}

/** 非流式响应 -> StreamChunk[]（思考 + 一次文本 + 恰好一个 done）。 */
export function mapAnthropicResponse(json: unknown): StreamChunk[] {
  const parsed = json as AnthropicResponse | null;
  if (parsed?.type === 'error' || parsed?.error) {
    return [{ type: 'error', error: parsed?.error?.message ?? 'Provider 返回错误', code: 'http' }];
  }

  const blocks = parsed?.content ?? [];
  const reasoning = blocks
    .filter((block) => block.type === 'thinking' && typeof block.thinking === 'string')
    .map((block) => block.thinking ?? '')
    .join('');
  const text = blocks
    .filter((block) => block.type === 'text' && typeof block.text === 'string')
    .map((block) => block.text ?? '')
    .join('');
  const toolCalls = blocks
    .filter((block) => block.type === 'tool_use' && typeof block.name === 'string')
    .map((block) => ({
      type: 'tool_call' as const,
      delta: JSON.stringify({
        index: 0,
        id: block.id,
        name: block.name,
        arguments: JSON.stringify(block.input ?? {}),
      }),
    }));

  const chunks: StreamChunk[] = [];
  if (reasoning) chunks.push({ type: 'reasoning', delta: reasoning });
  chunks.push({ type: 'text', delta: text });
  chunks.push(...toolCalls);

  const usage = toUsage(parsed?.usage);
  chunks.push(usage ? { type: 'done', usage } : { type: 'done' });
  return chunks;
}

function errorChunk(error: unknown): StreamChunk {
  const normalized = toProviderError(error);
  return {
    type: 'error',
    error: normalized.message,
    code: normalized.code,
    status: normalized.status,
  };
}

export class AnthropicAdapter implements ProviderAdapter {
  readonly id: string;
  readonly kind = ANTHROPIC_KIND;
  private readonly config: ResolvedProviderConfig;
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;
  private readonly retries: number;
  private readonly retryDelayMs: number;
  private readonly extraHeaders: Record<string, string>;

  constructor(config: ResolvedProviderConfig, deps?: ProviderDeps) {
    const resolved = resolveDeps(deps);
    this.config = config;
    this.id = config.id;
    this.fetchImpl = resolved.fetch;
    // 配置值优先，但一律过边界校验（负数 / NaN 不得绕过钳制）。
    this.timeoutMs = clampTimeoutMs(config.timeoutMs, resolved.timeoutMs);
    this.retries = clampRetries(config.retries, resolved.retries);
    this.retryDelayMs = clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs);
    // 自定义头：ProviderConfig.headers 覆盖 ProviderDeps.extraHeaders。
    this.extraHeaders = { ...resolved.extraHeaders, ...(config.headers ?? {}) };
  }

  private headers(stream: boolean): Record<string, string> {
    return anthropicHeaders({ stream, apiKey: this.config.apiKey, extra: this.extraHeaders });
  }

  private deps() {
    return {
      fetch: this.fetchImpl,
      timeoutMs: this.timeoutMs,
      retries: this.retries,
      retryDelayMs: this.retryDelayMs,
      extraHeaders: this.extraHeaders,
    };
  }

  async listModels(): Promise<string[]> {
    return readModelCatalog<{ data?: { id?: string }[]; has_more?: boolean; last_id?: string }>(anthropicModelsUrl(this.config.baseUrl), this.headers(false), this.deps(), json => {
      const models = (json.data ?? [])
        .map((item) => item.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
      if (json.has_more && !json.last_id) throw new ProviderHttpError('模型列表缺少分页游标', 'parse');
      return { models, ...(json.has_more ? { next: ['after_id', json.last_id!] as [string, string] } : {}) };
    });
  }

  async *chat(req: ChatRequest): AsyncIterable<StreamChunk> {
    // 契约：取消 = 静默结束，不产出任何分片（含 error）。
    if (req.signal?.aborted) return;

    let opened: OpenResponseResult;
    try {
      opened = await openResponseWithRetry({
        url: anthropicMessagesUrl(this.config.baseUrl),
        init: {
          method: 'POST',
          headers: this.headers(req.settings.stream),
          body: JSON.stringify(buildAnthropicBody(req)),
        },
        deps: this.deps(),
        externalSignal: req.signal,
      });
    } catch (error) {
      if (req.signal?.aborted) return;
      yield errorChunk(error);
      return;
    }

    const { response, guard } = opened;
    // 是否「提前」结束读取：提前结束时必须主动中止底层 fetch，释放悬挂连接。
    let earlyExit = false;

    try {
      if (!req.settings.stream) {
        const json = await readJsonBody(response);
        for (const chunk of mapAnthropicResponse(json)) yield chunk;
        return;
      }

      if (!response.body) {
        earlyExit = true;
        yield { type: 'error', error: '响应没有可读流', code: 'parse' };
        return;
      }

      let promptTokens: number | undefined;
      let completionTokens: number | undefined;

      for await (const payload of readSSEData(response.body, guard.signal)) {
        if (req.signal?.aborted) {
          earlyExit = true;
          return;
        }
        guard.reset();

        const parsed = mapAnthropicStreamPayload(payload);
        if (parsed.usage?.promptTokens !== undefined) promptTokens = parsed.usage.promptTokens;
        if (parsed.usage?.completionTokens !== undefined) completionTokens = parsed.usage.completionTokens;

        for (const chunk of parsed.chunks) {
          if (chunk.type === 'error') {
            earlyExit = true;
            yield chunk;
            return;
          }
          yield chunk;
        }

        if (parsed.stop) {
          // 服务端发完 message_stop 常常不关连接；提前退出并释放 body。
          earlyExit = true;
          break;
        }
      }

      if (req.signal?.aborted) {
        earlyExit = true;
        return;
      }
      if (guard.timedOut()) {
        earlyExit = true;
        yield { type: 'error', error: `请求超时（${this.timeoutMs}ms）`, code: 'timeout' };
        return;
      }

      // 无论服务端是否发送 message_stop，都在结束时补一次 done，保证上层收尾一致。
      const usage =
        promptTokens !== undefined || completionTokens !== undefined
          ? { promptTokens: promptTokens ?? 0, completionTokens: completionTokens ?? 0 }
          : undefined;
      yield usage ? { type: 'done', usage } : { type: 'done' };
    } catch (error) {
      earlyExit = true;
      if (req.signal?.aborted) return;
      if (guard.timedOut()) {
        yield { type: 'error', error: `请求超时（${this.timeoutMs}ms）`, code: 'timeout' };
        return;
      }
      if (isAbortError(error)) return;
      yield errorChunk(error);
    } finally {
      if (earlyExit) guard.abort();
      guard.dispose();
    }
  }
}

export function createAnthropicAdapter(
  config: ResolvedProviderConfig,
  deps?: ProviderDeps,
): ProviderAdapter {
  return new AnthropicAdapter(config, deps);
}
